import type { ProfileRow } from './types';
import type { LocationSearchResult } from '../../Store/StudentSlices/locationSearchSlice';
import LocationSearchField from './LocationSearchField';

type ProfileSectionProps = {
  data: {
    fullName: string;
    avatarUrl: string;
    avatarInitials: string;
    profileRows: ProfileRow[];
    location: string;
    locationDraft: string;
    locationResults: LocationSearchResult[];
    isLocationSearching: boolean;
    isEditingLocation: boolean;
    isEditingName: boolean;
    firstNameDraft: string;
    lastNameDraft: string;
    isSavingProfile: boolean;
    bio: string;
    bioDraft: string;
    isEditingBio: boolean;
    profileErrorMessage: string;
  };
  actions: {
    onUpload: () => void;
    onLocationChange: (value: string) => void;
    onEditLocation: () => void;
    onSaveLocation: () => void;
    onCancelLocation: () => void;
    onFirstNameChange: (value: string) => void;
    onLastNameChange: (value: string) => void;
    onEditName: () => void;
    onSaveName: () => void;
    onCancelName: () => void;
    onBioChange: (value: string) => void;
    onEditBio: () => void;
    onSaveBio: () => void;
    onCancelBio: () => void;
  };
};

const ProfileSection = ({ data, actions }: ProfileSectionProps) => {
  const {
    fullName,
    avatarUrl,
    avatarInitials,
    profileRows,
    location,
    locationDraft,
    locationResults,
    isLocationSearching,
    isEditingLocation,
    isEditingName,
    firstNameDraft,
    lastNameDraft,
    isSavingProfile,
    bio,
    bioDraft,
    isEditingBio,
    profileErrorMessage,
  } = data;
  const {
    onUpload,
    onLocationChange,
    onEditLocation,
    onSaveLocation,
    onCancelLocation,
    onFirstNameChange,
    onLastNameChange,
    onEditName,
    onSaveName,
    onCancelName,
    onBioChange,
    onEditBio,
    onSaveBio,
    onCancelBio,
  } = actions;

  return (
  <div className="flex flex-col gap-8">
    <div className="flex flex-wrap items-center justify-between gap-6">
      <div className="flex items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-slate-800 text-xl font-semibold text-white">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Profile"
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            avatarInitials
          )}
        </div>
      </div>
      <button
        type="button"
        className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
        onClick={onUpload}
      >
        Upload Photo
      </button>
    </div>
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="w-full max-w-[680px] flex-1">
          <p className="text-sm font-semibold text-slate-900">Full Name</p>
          {isEditingName ? (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={firstNameDraft}
                onChange={(event) => onFirstNameChange(event.target.value)}
                placeholder="First"
                className="min-w-[160px] rounded-[10px] border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 focus:border-blue-600 focus:outline-none"
              />
              <input
                type="text"
                value={lastNameDraft}
                onChange={(event) => onLastNameChange(event.target.value)}
                placeholder="Last"
                className="min-w-[160px] rounded-[10px] border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 focus:border-blue-600 focus:outline-none"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={onEditName}
              className="mt-1 text-sm font-semibold text-slate-500 hover:text-slate-700"
            >
              {fullName}
            </button>
          )}
        </div>
        {isEditingName ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSaveName}
              className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-blue-600 text-white disabled:cursor-not-allowed disabled:opacity-70"
              aria-label="Save name"
              disabled={isSavingProfile}
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m5 12 4 4L19 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onCancelName}
              className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-slate-300 text-slate-600"
              aria-label="Cancel name edit"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
            onClick={onEditName}
          >
            Edit Name
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-[680px]">
          <p className="text-sm font-semibold text-slate-900">Professional Bio</p>
          {isEditingBio ? (
            <textarea
              value={bioDraft}
              onChange={(event) => onBioChange(event.target.value)}
              rows={3}
              className="mt-2 w-full resize-none rounded-[12px] border border-slate-300 px-3 py-2 text-sm text-slate-600 outline-none focus:border-blue-600"
            />
          ) : (
            <p className="mt-1 text-sm text-slate-500" onClick={onEditBio}>
              {bio}
            </p>
          )}
        </div>
        <div className="flex items-center">
          {isEditingBio ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSaveBio}
                className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-blue-600 text-white disabled:cursor-not-allowed disabled:opacity-70"
                aria-label="Save bio"
                disabled={isSavingProfile}
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m5 12 4 4L19 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={onCancelBio}
                className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-slate-300 text-slate-600"
                aria-label="Cancel bio edit"
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
              onClick={onEditBio}
            >
              Edit Bio
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="w-full min-w-[280px] flex-1">
          <p className="text-sm font-semibold text-slate-900">Location</p>
          {isEditingLocation ? (
            <div className="mt-2 max-w-[360px]">
              <LocationSearchField
                value={locationDraft}
                options={locationResults}
                placeholder="Select location"
                onChange={onLocationChange}
                disabled={isSavingProfile}
                loading={isLocationSearching}
              />
            </div>
          ) : (
            <p className="mt-1 text-sm text-slate-500">{location}</p>
          )}
        </div>
        {isEditingLocation ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSaveLocation}
              className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-blue-600 text-white disabled:cursor-not-allowed disabled:opacity-70"
              aria-label="Save location"
              disabled={isSavingProfile}
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m5 12 4 4L19 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onCancelLocation}
              className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-slate-300 text-slate-600"
              aria-label="Cancel location edit"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
            onClick={onEditLocation}
          >
            Edit Location
          </button>
        )}
      </div>
      {profileRows.map((row) => (
        <div
          key={row.label}
          className="flex flex-wrap items-center justify-between gap-4"
        >
          <div className="max-w-[680px]">
            <p className="text-sm font-semibold text-slate-900">{row.label}</p>
            <p className="mt-1 text-sm text-slate-500">{row.value}</p>
          </div>
          {row.actionLabel ? (
            <button
              type="button"
              className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
            >
              {row.actionLabel}
            </button>
          ) : null}
        </div>
      ))}
      {profileErrorMessage ? (
        <p className="text-xs font-semibold text-rose-500">
          {profileErrorMessage}
        </p>
      ) : null}
    </div>
  </div>
  );
};

export default ProfileSection;
